import { formatCurrency } from "@/lib/format-data";

import type { CancellationPolicy } from "@/types/self-schedule";

interface FeeSpan {
  /** Exclusive lower edge, in hours before the appointment; 0 for the innermost span. */
  from: number;
  /** Inclusive upper edge, in hours before the appointment. */
  to: number;
  fee: number;
}

const describeSpan = ({ from, to }: FeeSpan, leading: boolean) =>
  from === 0
    ? `within ${to} hours${leading ? " of your appointment" : ""}`
    : `between ${from} and ${to} hours before${leading ? " your appointment" : ""}`;

/**
 * The acknowledgment a patient sees when their card goes on file for a
 * booking, worded from the policy the backend charges cancellations by.
 */
export const formatCardOnFilePolicy = ({
  windows,
  noShowFee,
}: CancellationPolicy): string => {
  // Neighboring windows with the same fee read as one span; free spans drop out.
  const spans: FeeSpan[] = [];
  windows.forEach((window, index) => {
    const from = windows[index + 1]?.hoursBefore ?? 0;
    const previous = spans.at(-1);
    if (previous && previous.fee === window.fee) {
      previous.from = from;
    } else {
      spans.push({ from, to: window.hoursBefore, fee: window.fee });
    }
  });
  const charged = spans.filter((span) => span.fee > 0);

  if (charged.length === 0 && noShowFee === 0) {
    return "I understand that my card will be saved on file.";
  }

  const sentences = [
    "I understand that my card will be saved on file and is subject to charge in accordance with our cancellation and no-show policies.",
  ];
  const [widest, ...narrower] = charged;
  if (
    widest &&
    narrower.length === 0 &&
    widest.from === 0 &&
    widest.fee === noShowFee
  ) {
    sentences.push(
      `Cancellations within ${widest.to} hours of your appointment as well as no-show appointments will be charged ${formatCurrency(noShowFee)}.`,
    );
  } else {
    if (widest) {
      const narrowerSpans = narrower
        .map(
          (span) =>
            `, or ${formatCurrency(span.fee)} ${describeSpan(span, false)}`,
        )
        .join("");
      sentences.push(
        `Cancellations ${describeSpan(widest, true)} will be charged ${formatCurrency(widest.fee)}${narrowerSpans}.`,
      );
    }
    if (noShowFee > 0) {
      sentences.push(
        `No-show appointments will be charged ${formatCurrency(noShowFee)}.`,
      );
    }
  }
  if (widest) {
    sentences.push(
      `Please contact us at least ${widest.to} hours before your appointment to make any changes.`,
    );
  }
  return sentences.join(" ");
};
